import React, { useState, useMemo, useEffect } from 'react';
import { UploadCloud, XCircle, Loader2 } from 'lucide-react';

import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Pagination } from '../../components/ui/Pagination';
import { FileIcon } from '../../components/ui/FileIcon';
import { getLocalDateString } from '../../utils/helpers';

const ITEMS_PER_PAGE = 5; // Groups per page

export const Archive = ({ archives, onSaveArchive, onDeleteArchive, fileHandler }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [storageDate, setStorageDate] = useState(getLocalDateString());
    const [deletingArchive, setDeletingArchive] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const groupedArchives = useMemo(() => {
        return archives.reduce((acc, file) => {
            const date = file.storage_date || 'Chưa phân loại';
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(file);
            return acc;
        }, {});
    }, [archives]);

    const sortedGroupKeys = useMemo(() => Object.keys(groupedArchives).sort((a,b) => new Date(b) - new Date(a)), [groupedArchives]);

    useEffect(() => {
        setCurrentPage(1);
    }, [sortedGroupKeys.length]);

    const paginatedGroupKeys = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedGroupKeys.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [sortedGroupKeys, currentPage]);


    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!selectedFile || !storageDate) {
            alert('Vui lòng chọn tệp và nhập ngày/chủ đề lưu trữ.');
            return;
        }
        setIsSubmitting(true);
        await onSaveArchive(selectedFile, storageDate);
        setSelectedFile(null);
        e.target.reset(); 
        setIsSubmitting(false);
    };

    return (
        <div className="space-y-8">
            <h2 className="text-3xl font-bold text-gray-800">Kho Lưu Trữ</h2>

            <form onSubmit={handleSave} className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Tải lên tệp mới</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input 
                        type="text" 
                        value={storageDate}
                        onChange={(e) => setStorageDate(e.target.value)}
                        placeholder="Ngày hoặc chủ đề (vd: 2025-06-14)"
                        className="p-3 border rounded-md"
                        required
                    />
                    <input 
                        type="file" 
                        onChange={handleFileChange} 
                        className="md:col-span-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        required
                    />
                </div>
                <div className="mt-4 flex justify-end">
                    <button type="submit" disabled={isSubmitting} className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-5 rounded-lg shadow-md hover:bg-blue-700 transition disabled:bg-blue-300">
                        {isSubmitting ? <Loader2 className="animate-spin" /> : <UploadCloud size={20} />}
                        {isSubmitting ? 'Đang tải lên...' : 'Lưu trữ'}
                    </button>
                </div>
            </form>

            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="space-y-6">
                    {paginatedGroupKeys.map(date => (
                        <div key={date}>
                            <h3 className="text-xl font-bold text-gray-700 mb-3 pb-2 border-b">{date}</h3>
                            <ul className="space-y-2">
                                {groupedArchives[date].map(file => (
                                    <li key={file.id} className="bg-white p-3 rounded-lg border flex justify-between items-center">
                                        <button onClick={() => fileHandler.open(file.file_url)} className="flex items-center gap-3 text-blue-600 hover:underline">
                                            <FileIcon attachmentUrl={file.file_url} />
                                            <span className="font-medium">{file.file_name}</span>
                                        </button>
                                        <button onClick={() => setDeletingArchive(file)} className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50">
                                            <XCircle size={20} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
                 <Pagination
                    currentPage={currentPage}
                    totalItems={sortedGroupKeys.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                />
            </div>

            <ConfirmationModal 
                isOpen={!!deletingArchive}
                onClose={() => setDeletingArchive(null)}
                onConfirm={() => {
                    onDeleteArchive(deletingArchive.id);
                    setDeletingArchive(null);
                }}
                title="Xác nhận Xóa Tệp"
                message={`Bạn có chắc muốn xóa tệp "${deletingArchive?.file_name}"?`}
            />
        </div>
    );
};
